import React, { useState } from "react";
import { Button } from "@mui/material";
import { Download } from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useLazyListGLSalesReportQuery } from "store/services/generalLedgerApi";

const ExportGLSalesReport = ({ filters, disabled = false }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportSalesReport] = useLazyListGLSalesReportQuery();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await exportSalesReport({
        ...filters,
        page: 1,
        limit: Number.MAX_SAFE_INTEGER,
      }).unwrap();
      const data = response?.data || [];

      if (!data.length) {
        toast.info("No data to export");
        return;
      }

      const headers = [
        "GL No",
        "Shipment ID",
        "Source Region/Province",
        "Source Branch",
        "Destination Region/Province",
        "Destination Branch",
        "Event",
        "Sales Amount (Rs.)",
        "GL Sales Date ",
        "Shipment Date",
      ];

      // NOTE: dates are formatted as plain UTC strings here (not JS Date
      // objects). SheetJS converts Date objects to Excel serials using the
      // *local* timezone of the browser running the export, which was
      // silently shifting UTC-created-at timestamps into the next calendar
      // day for users in UTC+ timezones (e.g. Asia/Kathmandu, UTC+5:45).
      // Formatting to strings ourselves in UTC keeps the export consistent
      // with what the backend actually filtered on.
      const rows = data.map((row) => [
        row.glNumber,
        row.shipment?.shipmentId,
        row.shipment?.sourceBranch?.region?.name || "N/A",
        row.shipment?.sourceBranch?.name || "N/A",
        row.shipment?.destinationBranch?.region?.name || "N/A",
        row.shipment?.destinationBranch?.name || "N/A",
        row.events,
        Number(row.salesAmount || 0),
        row.createdAt ? moment.utc(row.createdAt).format("YYYY-MM-DD") : "",
        row.shipment?.createdAt
          ? moment.utc(row.shipment.createdAt).format("YYYY-MM-DD")
          : "",
      ]);

      const netSalesRow = rows.length + 2;
      rows.push(["", "", "", "", "", "", "", "Net Sales", null]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      // Only the Sales Amount column needs numeric formatting now — the
      // date columns are plain text (UTC), so no date cell format (`z`) is
      // applied to them; applying one would misinterpret the string.
      for (let row = 1; row <= range.e.r; row += 1) {
        const salesAmountCell = XLSX.utils.encode_cell({ r: row, c: 7 });
        if (worksheet[salesAmountCell]) worksheet[salesAmountCell].z = "#,##0.00";
      }

      worksheet[`H${netSalesRow}`] = {
        t: "n",
        f: `SUM(H2:H${netSalesRow - 1})`,
        v: Number(response?.totalSales || 0),
        z: "#,##0.00",
      };

      worksheet["!cols"] = [
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 22 },
        { wch: 22 },
        { wch: 22 },
        { wch: 22 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
      ];
      worksheet["!autofilter"] = { ref: `A1:J${netSalesRow - 1}` };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GL Sales Report");
      XLSX.writeFile(
        workbook,
        `gl-sales-report-${moment().format("YYYY-MM-DD")}.xlsx`,
      );
      toast.success(`Exported ${data.length} records successfully`);
    } catch (error) {
      toast.error(error?.data?.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      startIcon={<Download />}
      variant="outlined"
      size="small"
      onClick={handleExport}
      disabled={disabled || isExporting}
    >
      {isExporting ? "Exporting..." : "Export"}
    </Button>
  );
};

export default ExportGLSalesReport;